/**
 * Site production board — display-only page for shop-floor TVs.
 *
 * Full-bleed, no nav chrome, no interaction. Capability-URL gated: the
 * `?key=<token>` query param is the credential, validated server-side on
 * every refresh, scoped to one site.
 *
 * Layout (Option B from the journey iteration): four stage columns
 * (prepress/printing/postpress/despatch) at full width, with a thin
 * "incoming from <other site>" ticker above the board that renders only
 * when there's something inbound — most of the day the board gets the
 * full screen. See docs/features/site-production-board/01_product_brief.md.
 *
 * Forced dark theme: this is a dedicated, always-on shop-floor screen,
 * not a page that should follow whatever theme a logged-in user last
 * picked — dark reads better at TV viewing distance and under
 * production-room lighting. The `dark` wrapper class flips the existing
 * semantic tokens (`bg-background`, `border-border`, `--color-chart-*`,
 * …) to their dark values; no colours are hardcoded here.
 *
 * The three production columns (prepress/printing/postpress) show "now
 * plus queued": jobs with a started, unfinished operation at the site
 * (`is_in_progress`, pulsing "In progress" badge), plus the single next
 * job waiting at each machine that currently has nothing running
 * (`is_in_progress: false`, static "Queued next" badge) — see
 * `_load_queued_job_nos` on the backend for the per-machine definition.
 * Despatch keeps its own ready/going/gone status badge, unaffected.
 *
 * States designed for: loading, populated (with/without incoming),
 * now-vs-queued mix, quiet floor (only ~1 job genuinely running, most
 * columns sparse — the realistic everyday state, not a broken board),
 * nearly-empty (today's board has cleared out), stale,
 * error/last-known-good, not-authorised.
 *
 * `SiteProductionBoardPage` is the reusable, site-parameterised page;
 * `ExeterBoardPage` is the thin per-site wrapper mounted at `/exeter`.
 * A future `/buckingham` route is just a second wrapper + route entry.
 */

import type { JSX, ReactNode, RefObject } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import {
  FileEdit,
  Layers,
  type LucideIcon,
  Printer,
  Radio,
  Scissors,
  Settings2,
  Truck,
} from "lucide-react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type {
  ActiveBundle,
  BoardOperator,
  DespatchColumnStatus,
  JobDespatchProgressView,
  ProductionStage,
  SiteBoardJob,
  SiteProductionBoardSnapshot,
} from "@shared-core/data/site-production-board-types";
import {
  STAGE_CHART_TOKEN,
  STAGE_LABELS,
  STAGE_ORDER,
  URGENCY_CHART_TOKEN,
  type UrgencyTier,
  classifyUrgency,
  groupJobsByStage,
} from "@shared-core/domain/site-production-board";
import { classifyFreshness, useNowTicker } from "@shared-core/domain/wallboard";
import { formatWeekdayDateOrDash } from "@shared-core/domain/formatters";

/** Shop-floor board tolerates a couple of minutes' lag fine. */
const REFRESH_INTERVAL_MS = 90 * 1000;
/** Incoming ticker cycle — slow enough to read a job number at a glance. */
const INCOMING_TICKER_DURATION_S = 30;
/** How long an overflowing column pauses at top/bottom before scrolling to the other end. */
const AUTO_SCROLL_PAUSE_MS = 4500;

const STAGE_ICON: Record<ProductionStage, LucideIcon> = {
  prepress: FileEdit,
  printing: Printer,
  postpress: Scissors,
  despatch: Truck,
};

/** `var(--chart-n)` for a stage, as a CSS colour value. */
function stageColor(stage: ProductionStage): string {
  return `var(${STAGE_CHART_TOKEN[stage]})`;
}

/** `var(--chart-n)` / `var(--destructive)` for an urgency tier, as a CSS colour value. */
function urgencyColor(tier: UrgencyTier): string {
  return `var(${URGENCY_CHART_TOKEN[tier]})`;
}

function urgencyLabel(tier: UrgencyTier, dueAtIso: string | null): string {
  if (tier === "overdue") return "Overdue";
  if (tier === "due_today") return "Due today";
  if (dueAtIso === null) return "No due date";
  const d = new Date(dueAtIso);
  return `Due ${d.toLocaleDateString("en-GB", { weekday: "short" })}`;
}

interface SiteProductionBoardPageProps {
  /** Tharstern site code, e.g. "EXETER01". */
  site: string;
  /** Human-readable label for this board's own site, e.g. "Exeter". */
  siteLabel: string;
  /** Label for the site the incoming panel watches, e.g. "Buckingham". */
  incomingFromSiteLabel: string;
}

export function SiteProductionBoardPage({
  site,
  siteLabel,
  incomingFromSiteLabel,
}: SiteProductionBoardPageProps): JSX.Element {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("key") ?? "";
  const adapter = useDataAdapter();

  const [snapshot, setSnapshot] = useState<SiteProductionBoardSnapshot | null>(
    null,
  );
  const [phase, setPhase] = useState<
    "loading" | "ready" | "unauthorised" | "errored"
  >("loading");
  const lastGoodRef = useRef<SiteProductionBoardSnapshot | null>(null);

  const fetchSnapshot = useCallback(async () => {
    if (!token) {
      setPhase("unauthorised");
      return;
    }
    try {
      const result = await adapter.getSiteProductionBoard(site, token);
      setSnapshot(result);
      lastGoodRef.current = result;
      setPhase("ready");
    } catch {
      // Degrade to last-known-good rather than blanking an unattended screen.
      if (lastGoodRef.current) {
        setSnapshot(lastGoodRef.current);
      }
      setPhase("errored");
    }
  }, [adapter, site, token]);

  useEffect(() => {
    void fetchSnapshot();
    const id = setInterval(() => void fetchSnapshot(), REFRESH_INTERVAL_MS);
    return () => clearInterval(id);
  }, [fetchSnapshot]);

  if (phase === "loading") return <LoadingState />;
  if (phase === "unauthorised") return <NotAuthorisedState />;
  if (!snapshot) return <ErrorNoLastGoodState />;

  return (
    <PopulatedBoard
      snapshot={snapshot}
      siteLabel={siteLabel}
      incomingFromSiteLabel={incomingFromSiteLabel}
      degraded={phase === "errored"}
    />
  );
}

/** Thin wrapper mounted at `/exeter` — Buckingham gets an equivalent wrapper later. */
export function ExeterBoardPage(): JSX.Element {
  return (
    <SiteProductionBoardPage
      site="EXETER01"
      siteLabel="Exeter"
      incomingFromSiteLabel="Buckingham"
    />
  );
}

// ---------------------------------------------------------------- States ---

function LoadingState(): JSX.Element {
  return (
    <FullBleed>
      <div className="flex-1 flex items-center justify-center text-muted-foreground text-3xl">
        Loading…
      </div>
    </FullBleed>
  );
}

function NotAuthorisedState(): JSX.Element {
  return (
    <FullBleed>
      <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground gap-4">
        <div className="text-5xl">Screen not authorised</div>
        <div className="text-xl opacity-70">
          This board URL is missing or no longer valid.
        </div>
      </div>
    </FullBleed>
  );
}

function ErrorNoLastGoodState(): JSX.Element {
  return (
    <FullBleed>
      <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground gap-4">
        <div className="text-4xl">Board temporarily unavailable</div>
        <div className="text-lg opacity-70">Retrying…</div>
      </div>
    </FullBleed>
  );
}

// --------------------------------------------------------- Populated board ---

function PopulatedBoard({
  snapshot,
  siteLabel,
  incomingFromSiteLabel,
  degraded,
}: {
  snapshot: SiteProductionBoardSnapshot;
  siteLabel: string;
  incomingFromSiteLabel: string;
  degraded: boolean;
}): JSX.Element {
  const now = useNowTicker();
  const freshness = classifyFreshness(snapshot.as_at, now);
  const grouped = groupJobsByStage(snapshot.jobs);

  return (
    <FullBleed>
      <PageHeader siteLabel={siteLabel} now={now} />
      {snapshot.incoming.jobs.length > 0 && (
        <IncomingTicker
          sourceSiteLabel={incomingFromSiteLabel}
          jobs={snapshot.incoming.jobs}
        />
      )}
      <StageColumns grouped={grouped} now={now} />
      <PageFooter
        asAt={snapshot.as_at}
        freshness={freshness}
        degraded={degraded}
      />
    </FullBleed>
  );
}

function PageHeader({
  siteLabel,
  now,
}: {
  siteLabel: string;
  now: number;
}): JSX.Element {
  const d = new Date(now);
  const dateLabel = formatWeekdayDateOrDash(d);
  const timeLabel = `${d.getHours().toString().padStart(2, "0")}:${d
    .getMinutes()
    .toString()
    .padStart(2, "0")}`;

  return (
    <header className="flex items-baseline justify-between px-12 pt-8 pb-5">
      <div className="flex items-baseline gap-4">
        <span
          aria-hidden
          className="inline-block size-3 rounded-full animate-pulse"
          style={{ backgroundColor: "var(--chart-2)" }}
        />
        <div className="text-3xl font-semibold tracking-wide uppercase">
          {siteLabel} <span className="opacity-50">·</span> Production Board
        </div>
      </div>
      <div className="text-2xl opacity-70 tabular-nums">
        {dateLabel} · {timeLabel}
      </div>
    </header>
  );
}

function IncomingTicker({
  sourceSiteLabel,
  jobs,
}: {
  sourceSiteLabel: string;
  jobs: readonly { job_no: string; stage: ProductionStage }[];
}): JSX.Element {
  // Duplicated so the loop from translateX(0) to translateX(-50%) joins
  // seamlessly — same technique as the performance wallboard's ticker.
  const duplicated = [...jobs, ...jobs];

  return (
    <section
      aria-label={`Jobs incoming from ${sourceSiteLabel}`}
      className="flex items-center gap-6 border-y border-border bg-card/40 overflow-hidden"
    >
      <div className="flex items-center gap-2 pl-12 py-3 shrink-0 text-base uppercase tracking-widest text-muted-foreground whitespace-nowrap">
        <Truck aria-hidden className="size-4" />
        Incoming from {sourceSiteLabel} ({jobs.length})
      </div>
      <div className="flex-1 overflow-hidden">
        <div
          className="flex gap-4 will-change-transform"
          style={{
            animation: `wallboard-ticker ${INCOMING_TICKER_DURATION_S}s linear infinite`,
            width: "max-content",
          }}
        >
          {duplicated.map((job, i) => (
            <span
              // Index makes the duplicated set unique without changing key semantics.
              key={`${job.job_no}-${i}`}
              className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-1.5 text-base tabular-nums whitespace-nowrap"
            >
              <span
                aria-hidden
                className="size-2 rounded-full"
                style={{ backgroundColor: stageColor(job.stage) }}
              />
              #{job.job_no}
              <span className="opacity-60">{STAGE_LABELS[job.stage]}</span>
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

function StageColumns({
  grouped,
  now,
}: {
  grouped: Record<ProductionStage, SiteBoardJob[]>;
  now: number;
}): JSX.Element {
  return (
    <section className="flex-1 min-h-0 grid grid-cols-4 gap-px bg-border overflow-hidden">
      {STAGE_ORDER.map((stage) => (
        <StageColumn
          key={stage}
          stage={stage}
          jobs={grouped[stage]}
          now={now}
        />
      ))}
    </section>
  );
}

function StageColumn({
  stage,
  jobs,
  now,
}: {
  stage: ProductionStage;
  jobs: readonly SiteBoardJob[];
  now: number;
}): JSX.Element {
  const Icon = STAGE_ICON[stage];
  const color = stageColor(stage);

  const scrollRef = useAutoScrollColumn(jobs.length);

  return (
    // min-h-0: a grid item's default min-height is `auto` (fits its
    // content), which silently defeats the child's overflow-y-auto —
    // the column grows to hold every card instead of clipping and
    // scrolling. Same fix applies one level down to the scroll
    // container itself, since it's a flex item of this column.
    <div
      className="bg-background flex flex-col min-h-0 overflow-hidden border-t-4"
      style={{ borderTopColor: color }}
    >
      <div className="px-6 py-5 flex items-center justify-between border-b border-border">
        <div className="flex items-center gap-3">
          <Icon aria-hidden className="size-6" style={{ color }} />
          <span className="text-xl uppercase tracking-widest">
            {STAGE_LABELS[stage]}
          </span>
        </div>
        <span
          className="text-lg tabular-nums font-semibold rounded-full min-w-9 h-9 px-2 flex items-center justify-center"
          style={{
            backgroundColor: `color-mix(in oklch, ${color} 18%, transparent)`,
          }}
        >
          {jobs.length}
        </span>
      </div>
      <div
        ref={scrollRef}
        className="flex-1 min-h-0 overflow-y-auto p-4 flex flex-col gap-3"
      >
        {jobs.length === 0 ? (
          <EmptyColumn stage={stage} />
        ) : (
          jobs.map((job) => <JobCard key={job.job_no} job={job} now={now} />)
        )}
      </div>
    </div>
  );
}

const AUTO_SCROLL_STEP_MS = 30;
/** Constant scroll speed, not a constant duration — a duration fixed
 * regardless of distance made a busy column (many cards, long scroll
 * distance) fly past far faster than a quiet one, which is unreadable
 * on a shop-floor TV nobody is standing at to re-read. ~1 card of
 * height (~150px) every 2s is a pace someone can actually read while
 * it moves. */
const AUTO_SCROLL_PX_PER_MS = 0.075;
const AUTO_SCROLL_MIN_TRANSITION_MS = 900;

/**
 * Animate `el.scrollTop` from its current value to `target`, driven by
 * `setInterval` rather than the native `Element.scrollTo({behavior:
 * "smooth"})`. Signage hardware varies widely (embedded WebViews, older
 * browser engines), and native smooth scroll is also throttled or fully
 * paused on backgrounded/hidden pages in Chromium — verified directly
 * against this codebase's own preview tooling, which reports
 * `document.hidden` for its automated tabs. `setInterval` + a direct
 * `scrollTop` assignment keeps working regardless, and is simple enough
 * to not need a library.
 *
 * Calls `onDone` after the last step rather than on a fixed timer, so
 * the caller can chain the next stage (pause, then scroll back) off
 * actual completion — see `useAutoScrollColumn` for why that matters.
 * Returns a cancel function so an unmounting effect can stop mid-animation.
 */
function animateScrollTo(
  el: HTMLElement,
  target: number,
  onDone: () => void,
): () => void {
  const start = el.scrollTop;
  const distance = target - start;
  const durationMs = Math.max(
    AUTO_SCROLL_MIN_TRANSITION_MS,
    Math.abs(distance) / AUTO_SCROLL_PX_PER_MS,
  );
  const steps = Math.max(1, Math.round(durationMs / AUTO_SCROLL_STEP_MS));
  let step = 0;
  const id = setInterval(() => {
    step += 1;
    const progress = Math.min(1, step / steps);
    el.scrollTop = start + distance * progress;
    if (progress >= 1) {
      clearInterval(id);
      onDone();
    }
  }, AUTO_SCROLL_STEP_MS);
  return () => clearInterval(id);
}

/**
 * On a busy day a stage column can hold more jobs than fit on screen.
 * There's no one at the TV to scroll it, so once a column overflows we
 * auto-scroll it to the bottom, pause, then back to the top — a
 * digital-signage pattern, distinct from the incoming ticker's seamless
 * horizontal loop because job cards vary in height and change as the
 * board refreshes, so a duplicate-content loop can't stay seamless here.
 *
 * Chains stages off `animateScrollTo`'s completion callback rather than
 * a fixed-interval retrigger: a constant-speed scroll's duration grows
 * with distance, so a fixed retrigger could fire again — starting a
 * second, overlapping animation — before a long scroll had finished,
 * producing exactly the jumpy, fighting-scrollTop behaviour this was
 * meant to fix. Chaining guarantees at most one animation runs at a time.
 *
 * Re-checks overflow every cycle rather than once on mount: a column
 * that currently fits stays static (no pointless motion), and one that
 * grows past the fold on a later poll picks up scrolling without a
 * remount. `dependencyKey` (job count) restarts the cycle cleanly when
 * a column's content changes shape.
 */
function useAutoScrollColumn(
  dependencyKey: number,
): RefObject<HTMLDivElement | null> {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let cancelled = false;
    let atBottom = false;
    let cancelAnimation: (() => void) | null = null;
    let pauseId: ReturnType<typeof setTimeout> | null = null;

    // Takes `target` as a param (rather than closing over the outer `el`)
    // because TS does not retain null-narrowing across a closure boundary.
    function cycle(target: HTMLElement): void {
      if (cancelled) return;
      const maxScroll = target.scrollHeight - target.clientHeight;
      if (maxScroll <= 4) {
        // content fits right now — recheck next cycle in case it grows
        pauseId = setTimeout(() => cycle(target), AUTO_SCROLL_PAUSE_MS);
        return;
      }
      atBottom = !atBottom;
      cancelAnimation = animateScrollTo(
        target,
        atBottom ? maxScroll : 0,
        () => {
          pauseId = setTimeout(() => cycle(target), AUTO_SCROLL_PAUSE_MS);
        },
      );
    }

    pauseId = setTimeout(() => cycle(el), AUTO_SCROLL_PAUSE_MS);

    return () => {
      cancelled = true;
      if (pauseId) clearTimeout(pauseId);
      if (cancelAnimation) cancelAnimation();
    };
  }, [dependencyKey]);

  return ref;
}

function EmptyColumn({ stage }: { stage: ProductionStage }): JSX.Element {
  // Despatch empty reads as a positive "all shipped" signal rather than
  // the neutral "nothing here yet" of the other three stages.
  const label =
    stage === "despatch" ? "All clear — nothing to ship" : "No jobs";
  return (
    <div className="text-base text-muted-foreground px-2 py-8 text-center">
      {label}
    </div>
  );
}

/** Ready/going/gone label + dot colour — the despatch column's compact
 * summary (API guidance: full per-step detail is optional). */
const DESPATCH_STATUS_LABEL: Record<DespatchColumnStatus, string> = {
  ready: "Ready to go",
  going: "Going out",
  gone: "Gone",
};
const DESPATCH_STATUS_TOKEN: Record<DespatchColumnStatus, string> = {
  ready: "--muted-foreground",
  going: "--chart-3",
  gone: "--chart-2",
};

function DespatchStatusBadge({
  despatch,
}: {
  despatch: JobDespatchProgressView;
}): JSX.Element {
  return (
    <div className="flex items-center gap-2 text-sm">
      <span
        aria-hidden
        className="inline-block size-2 rounded-full"
        style={{
          backgroundColor: `var(${DESPATCH_STATUS_TOKEN[despatch.status]})`,
        }}
      />
      <span className="font-medium">
        {DESPATCH_STATUS_LABEL[despatch.status]}
      </span>
      {despatch.current_operator_display_name && (
        <span className="text-muted-foreground">
          · {despatch.current_operator_display_name}
        </span>
      )}
    </div>
  );
}

/** One operator pill — a filled live-dot marks the kiosk-live source so
 * "who's actually on it right now" reads at a glance vs. the
 * synced-timesheet fallback. */
function OperatorPill({ operator }: { operator: BoardOperator }): JSX.Element {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-border px-2.5 py-1 text-xs">
      {operator.source === "kiosk_live" ? (
        <Radio
          aria-hidden
          className="size-3"
          style={{ color: "var(--chart-2)" }}
        />
      ) : null}
      {operator.display_name}
    </span>
  );
}

/**
 * Left-border colour is urgency (due date), not stage — the column
 * already carries stage identity (icon, header colour, position), so
 * repeating it per-card would waste the one accent slot on redundant
 * information. Urgency is the signal that varies within a column and
 * is genuinely new per job — same red/amber/green rule (§ domain
 * `classifyUrgency`) as the real daily production list this mirrors.
 *
 * Gone-today jobs (`is_gone`) render at reduced opacity with a "Gone"
 * marker instead of the urgency badge — retained for end-of-day
 * visibility, but visually out of the active flow.
 */
function JobCard({
  job,
  now,
}: {
  job: SiteBoardJob;
  now: number;
}): JSX.Element {
  const tier = classifyUrgency(job.due_at, now);
  const color = urgencyColor(tier);
  const toneClass =
    tier === "future" ? "text-muted-foreground" : "font-semibold";
  const hasLiveOperator = job.operators.some((o) => o.source === "kiosk_live");

  return (
    <article
      className={`rounded-xl border border-border bg-card/50 p-4 pl-5 flex flex-col gap-1 border-l-4 ${
        job.is_gone ? "opacity-60" : ""
      }`}
      style={{ borderLeftColor: job.is_gone ? undefined : color }}
    >
      <div className="flex items-baseline justify-between gap-3">
        <span className="flex items-baseline gap-2">
          <span className="text-lg font-semibold tabular-nums">
            #{job.job_no}
          </span>
          {job.is_gone ? (
            <span className="text-xs uppercase tracking-wide text-muted-foreground">
              Gone
            </span>
          ) : job.is_in_progress ? (
            <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wide text-muted-foreground">
              <span
                aria-hidden
                className="inline-block size-2 rounded-full animate-pulse"
                style={{
                  backgroundColor: hasLiveOperator
                    ? "var(--chart-2)"
                    : "var(--muted-foreground)",
                }}
              />
              In progress
            </span>
          ) : (
            // Despatch cards carry their own ready/going status via
            // DespatchStatusBadge below — this "next up" badge is only
            // for the three production columns' queued jobs (see
            // `_load_queued_job_nos`: one job per machine, waiting).
            job.stage !== "despatch" && (
              <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wide text-muted-foreground">
                <span
                  aria-hidden
                  className="inline-block size-2 rounded-full border border-muted-foreground"
                />
                Queued next
              </span>
            )
          )}
        </span>
        {!job.is_gone && (
          <span
            className={`text-sm tabular-nums ${toneClass}`}
            style={{ color: tier === "future" ? undefined : color }}
          >
            {urgencyLabel(tier, job.due_at)}
          </span>
        )}
      </div>
      <div className="text-base leading-snug">{job.description}</div>
      <div className="text-sm text-muted-foreground">
        {job.customer_name} · {job.quantity.toLocaleString("en-GB")}
      </div>
      <div className="text-sm text-muted-foreground">
        {job.current_stage}
        {job.next_stage && (
          <span className="opacity-60"> → {job.next_stage}</span>
        )}
      </div>
      {(job.operators.length > 0 || job.machine_name) && (
        <div className="flex items-center gap-2 flex-wrap pt-1">
          {job.machine_name && (
            <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
              <Settings2 aria-hidden className="size-3.5" />
              {job.machine_name}
            </span>
          )}
          {job.operators.map((operator) => (
            <OperatorPill key={operator.display_name} operator={operator} />
          ))}
        </div>
      )}
      {job.active_bundle && <ActiveBundleChips bundle={job.active_bundle} />}
      {job.despatch && <DespatchStatusBadge despatch={job.despatch} />}
    </article>
  );
}

/**
 * Bundle name + member chips (Option B — module update spec 06 §11):
 * the board never represents in-progress bundle work as a single mystery
 * operation. Chips, not a stepper — a bundle books as one working
 * operation, so no member reads as individually "live" (product brief
 * §11). Matches the kiosk's own bundle-chip style
 * (`ShopfloorKioskPage.tsx`) for visual consistency across the two
 * surfaces.
 */
function ActiveBundleChips({ bundle }: { bundle: ActiveBundle }): JSX.Element {
  return (
    <div className="flex flex-wrap items-center gap-1.5 pt-1">
      <span className="inline-flex items-center gap-1 text-xs font-medium text-primary">
        <Layers aria-hidden className="size-3.5" />
        {bundle.name}
      </span>
      {bundle.members.map((member, index) => (
        <span
          key={`${member.label}-${index}`}
          className={`rounded-full border border-border bg-card px-2 py-0.5 text-xs text-muted-foreground ${
            member.is_outstanding ? "" : "opacity-50 line-through"
          }`}
        >
          {member.label}
        </span>
      ))}
    </div>
  );
}

// ----------------------------------------------------------------- Footer ---

// Raw tokens, not the `--color-*` `@theme inline` aliases — see the note
// on `STAGE_CHART_TOKEN` for why the alias is frozen to light-mode here.
const FRESHNESS_DOT_COLOR: Record<"fresh" | "stale" | "ancient", string> = {
  fresh: "var(--chart-2)",
  stale: "var(--chart-3)",
  ancient: "var(--destructive)",
};

function PageFooter({
  asAt,
  freshness,
  degraded,
}: {
  asAt: string;
  freshness: "fresh" | "stale" | "ancient";
  degraded: boolean;
}): JSX.Element {
  const d = new Date(asAt);
  const timeLabel = `${d.getHours().toString().padStart(2, "0")}:${d
    .getMinutes()
    .toString()
    .padStart(2, "0")}`;
  const freshnessLabel =
    freshness === "fresh"
      ? `Updated ${timeLabel}`
      : freshness === "stale"
        ? `Stale — last updated ${timeLabel}`
        : `Last-known-good — ${timeLabel}`;
  const toneClass = freshness === "fresh" ? "text-muted-foreground" : "";

  return (
    <footer className="flex items-center justify-end gap-3 px-12 py-4 text-base">
      <span
        aria-hidden
        className="size-2.5 rounded-full"
        style={{ backgroundColor: FRESHNESS_DOT_COLOR[freshness] }}
      />
      <div className={toneClass}>
        {freshnessLabel}
        {degraded && " · using last known data"}
      </div>
    </footer>
  );
}

// ------------------------------------------------------------------ Shell ---

function FullBleed({ children }: { children: ReactNode }): JSX.Element {
  // h-screen + overflow-hidden, not min-h-screen: this is an unattended
  // kiosk, so the *page* must never grow taller than the TV and scroll
  // as a whole — min-h-screen only floors the height, it doesn't cap it,
  // which silently defeats every column's internal overflow-y-auto (the
  // page grows to fit content instead of the column clipping and
  // scrolling). Capping here is what makes min-h-0 further down the
  // flex/grid chain actually bite.
  return (
    <div className="dark h-screen w-full overflow-hidden flex flex-col bg-background text-foreground select-none">
      {children}
    </div>
  );
}
